import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useEntity, GROUP_VALUE } from "@/lib/entityStore";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ProductionBadge } from "@/components/ds/ProductionBadge";
import { LogOut } from "lucide-react";

export default function Topbar() {
    const { user, logout } = useAuth();
    const { entities, selectedEntityId, selectEntity } = useEntity();
    const nav = useNavigate();

    // Group-level data_mode: live if ANY entity is live; otherwise pick the worst
    // (error → pending → mock). Calm — informative, not alarming.
    const groupMode = (() => {
        if (!entities || entities.length === 0) return "mock";
        if (entities.some((e) => e.data_mode === "production_error")) return "production_error";
        if (entities.some((e) => e.data_mode === "production_live")) return "production_live";
        if (entities.some((e) => e.data_mode === "production_pending")) return "production_pending";
        return "mock";
    })();

    const handleLogout = () => {
        logout();
        nav("/login");
    };

    return (
        <header
            className="h-14 sticky top-0 z-40 bg-[#fafaf9]/95 backdrop-blur-sm border-b border-[#e7e5e4] flex items-center justify-between px-6"
            data-testid="app-topbar"
        >
            <div className="flex items-center gap-3">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">
                    Viewing
                </div>
                <Select
                    value={selectedEntityId}
                    onValueChange={selectEntity}
                    data-testid="entity-selector"
                >
                    <SelectTrigger
                        className="w-64 h-9 rounded-md bg-white border-stone-200 text-[13px] focus:ring-0 focus:ring-offset-0 hover:border-stone-300 transition-colors"
                        data-testid="entity-selector-trigger"
                    >
                        <SelectValue placeholder="Select entity" />
                    </SelectTrigger>
                    <SelectContent className="rounded-md">
                        <SelectItem
                            value={GROUP_VALUE}
                            data-testid="entity-option-group"
                        >
                            <span className="font-medium">
                                All Companies — Group View
                            </span>
                        </SelectItem>
                        {entities.map((e) => (
                            <SelectItem
                                key={e.id}
                                value={e.id}
                                data-testid={`entity-option-${e.entity_number}`}
                            >
                                <span className="inline-flex items-center gap-2">
                                    <span>{e.display_name}</span>
                                </span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="flex items-center gap-4">
                <ProductionBadge state={groupMode} />
                <div className="hidden md:flex flex-col items-end leading-tight">
                    <div className="text-[12px] text-stone-600 font-medium">
                        {user?.full_name || "Admin"}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-400">
                        {user?.role}
                    </div>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-md h-9 px-2.5 text-stone-500 hover:text-stone-800 hover:bg-stone-100"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                >
                    <LogOut size={14} />
                </Button>
            </div>
        </header>
    );
}
